import { type ComponentPropsWithRef, useState } from 'react';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import {
  Combobox as HeadlessCombobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption as HeadlessComboboxOption,
} from '@headlessui/react';
import { cn } from './lib/utils';

export interface ComboboxOption {
  value: string | number;
  label: string;
}

export type ComboboxProps = {
  name: string;
  options: ComboboxOption[];
  placeholder?: string;
  defaultValue?: string;
  invalid?: boolean;
} & ComponentPropsWithRef<typeof HeadlessCombobox>;

export function Combobox({ name, options, placeholder, defaultValue, invalid = false, ...props }: ComboboxProps) {
  const [query, setQuery] = useState('');

  const filteredOptions =
    query === ''
      ? options
      : options.filter((item: ComboboxOption) => {
          return item.label.toLowerCase().includes(query.toLowerCase());
        });

  return (
    <HeadlessCombobox name={name} defaultValue={defaultValue} as="div" data-slot="control" {...props}>
      <div className="relative">
        <ComboboxInput
          className={cn(
            'text-gray-12 border-gray-2 ring-gray-2 focus:ring-bright-blue-7 dark:focus:ring-bright-blue-3 w-full rounded-md border bg-white py-0 pl-3 pr-10 leading-10 shadow-sm focus:ring-2 focus:ring-inset dark:border-white/10 dark:bg-white/5 dark:text-white',
            invalid
              ? '!dark:bg-red-100 border-red-500 !bg-red-100 ring-red-500 dark:border-red-500 dark:ring-red-500'
              : '',
          )}
          onChange={(event) => setQuery(event.target.value)}
          displayValue={(item: string) => options.find(({ value }) => value === item)?.label ?? ''}
          placeholder={placeholder}
        ></ComboboxInput>
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronsUpDownIcon className="text-gray-4 h-5 w-5" aria-hidden="true" />
        </ComboboxButton>
        <ComboboxOptions className="dark:bg-gray-10 absolute z-10 mt-1 max-h-60 w-full list-none overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {filteredOptions.map((option: ComboboxOption) => (
            <HeadlessComboboxOption
              key={option.value}
              value={option.value}
              className={({ focus }) =>
                cn(
                  'relative cursor-default select-none py-2 pl-3 pr-9',
                  focus ? 'bg-bright-blue text-white' : 'text-gray-12 dark:text-white',
                )
              }
            >
              {({ focus, selected }) => (
                <>
                  <span className={cn('block truncate', selected && 'font-semibold')}>{option.label}</span>
                  {selected && (
                    <span
                      className={cn(
                        'absolute inset-y-0 right-0 flex items-center pr-4',
                        focus ? 'bg-bright-blue text-white' : 'text-bright-blue dark:text-bright-blue-3',
                      )}
                    >
                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  )}
                </>
              )}
            </HeadlessComboboxOption>
          ))}
        </ComboboxOptions>
      </div>
    </HeadlessCombobox>
  );
}
